import { RefreshSchedule } from "event-definitions"
import { noChange } from "lib/@hooks/useRefresh"
import { refreshId } from "lib/graphql/api"
import { registerVersionInfoPlug } from "routes/schedule/lib/version-info"
import { getSchedule } from "routes/schedule/lib/get-schedule"

registerVersionInfoPlug()

export const dummySchedule = {}

export const SCHEDULE_UNAVAILABLE =
    "This schedule isn't available to view. It may not be part of your licence, or it may belong to another account."

export const SCHEDULE_LOAD_ERROR =
    "Something went wrong loading this schedule. Refresh the page to try again; if it keeps happening, contact support."

/**
 * Reads a schedule for callers that only need the record itself.
 *
 * This is the light path and stays on plain `useResults`: it goes through `processVariables`, so
 * the `refreshId` token is honoured as a refresh filter rather than passed to the query as a
 * stray argument. Rendered surfaces like the regime tree mount one of these per schedule row,
 * so this hook must not carry the extra per-instance state the status variant needs.
 *
 * Use `useScheduleWithStatus` when a view has to tell "loading" from "unavailable" from "failed".
 */
export function useSchedule(code, version = "no_v") {
    const refresh = RefreshSchedule.useRefresh(noChange)
    const schedule = getSchedule.useResults(code, version, refreshId(refresh.id)) ?? dummySchedule

    return {
        loading: schedule === dummySchedule,
        schedule,
    }
}

/**
 * Reads a schedule and resolves every terminal outcome of the query, for the full-page schedule
 * views that would otherwise sit on "Loading Template" forever (FIQ-1863):
 *   - data       → show the schedule
 *   - clean null → hidden by the licensing / client-isolation filters, or not found
 *   - error      → the query failed (e.g. field resolvers choking on sparse migrated records)
 *
 * Only for single-instance views. `useResults.status` is heavier per instance and does not run
 * `processVariables`, so do not reach for it from anything rendered once per row in a list.
 */
export function useScheduleWithStatus(code, version = "no_v") {
    const refresh = RefreshSchedule.useRefresh(noChange)
    const { data: schedule, loading, error } = getSchedule.useResults.status(code, version, refreshId(refresh.id))

    // Only report loading while there is nothing to show yet, so invalidation refetches don't
    // blank an already-visible schedule.
    const busy = !!code && loading && schedule == null && !error

    return {
        loading: busy,
        error: deriveError(),
        schedule: schedule ?? dummySchedule,
    }

    function deriveError() {
        if (!code || busy) return undefined
        if (error) return SCHEDULE_LOAD_ERROR
        if (schedule == null && !loading) return SCHEDULE_UNAVAILABLE
        return undefined
    }
}
